import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CalendarDays, UserRound } from 'lucide-react';
import Header from '@/components/layout/Header';
import FooterNew from '@/components/layout/FooterNew';
import BlogPostContent from '@/components/blog/BlogPostContent';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { API } from '@/App';
import { usePublicSeo, PUBLIC_SITE_URL } from '@/utils/seo';
import { formatBlogDate } from '@/utils/blog';

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const articleStructuredData = useMemo(() => {
    if (!post?.slug) return [];

    const articleUrl = `${PUBLIC_SITE_URL}/blog/${post.slug}`;
    return [
      {
        id: 'blog-posting',
        data: {
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          '@id': `${articleUrl}#article`,
          headline: post.title,
          description: post.meta_description || post.excerpt,
          url: articleUrl,
          inLanguage: 'fr',
          datePublished: post.created_at,
          dateModified: post.updated_at || post.created_at,
          author: {
            '@type': 'Organization',
            name: post.author || 'Equipe Coli Noki',
          },
          publisher: { '@id': `${PUBLIC_SITE_URL}/#organization` },
          articleSection: post.category || 'Logistique',
          image: post.cover_image_url ? [post.cover_image_url] : undefined,
          mainEntityOfPage: articleUrl,
        },
      },
      {
        id: 'blog-post-breadcrumb',
        data: {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Accueil',
              item: `${PUBLIC_SITE_URL}/`,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Blog',
              item: `${PUBLIC_SITE_URL}/blog`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: post.title,
              item: articleUrl,
            },
          ],
        },
      },
    ];
  }, [post]);

  usePublicSeo({
    title: post?.meta_title || 'Blog Coli Noki',
    description: post?.meta_description || 'Article de blog Coli Noki.',
    path: slug ? `/blog/${slug}` : '/blog',
    type: 'article',
    structuredData: articleStructuredData,
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [slug]);

  useEffect(() => {
    let active = true;

    const loadPost = async () => {
      setLoading(true);
      setNotFound(false);
      setLoadError(false);
      try {
        const response = await axios.get(`${API}/blog/posts/${encodeURIComponent(String(slug || '').trim())}`);
        if (active) {
          setPost(response.data || null);
        }
      } catch (error) {
        if (active) {
          setPost(null);
          const isNotFound = error?.response?.status === 404;
          setNotFound(isNotFound);
          setLoadError(!isNotFound);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadPost();
    return () => {
      active = false;
    };
  }, [slug]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      <main className="py-12 sm:py-16">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <nav aria-label="Fil d'Ariane" className="mb-5 flex flex-wrap items-center gap-2 text-sm text-slate-500">
            <Link to="/" className="hover:text-red-600">Accueil</Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-red-600">Blog</Link>
            {post?.title ? (
              <>
                <span>/</span>
                <span className="text-slate-700">{post.title}</span>
              </>
            ) : null}
          </nav>
          <Button asChild variant="ghost" className="mb-6 rounded-full px-0 text-red-600 hover:bg-transparent hover:text-red-700">
            <Link to="/blog">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour au blog
            </Link>
          </Button>

          {loading ? (
            <Card className="rounded-[2rem] border border-slate-200 bg-white">
              <CardContent className="space-y-6 p-8">
                <div className="h-72 animate-pulse rounded-[1.6rem] bg-slate-100" />
                <div className="h-5 w-40 animate-pulse rounded-full bg-slate-100" />
                <div className="h-14 w-full animate-pulse rounded-3xl bg-slate-100" />
                <div className="h-56 w-full animate-pulse rounded-3xl bg-slate-100" />
              </CardContent>
            </Card>
          ) : notFound ? (
            <Card className="rounded-[2rem] border border-slate-200 bg-white shadow-sm">
              <CardContent className="space-y-4 px-8 py-16 text-center">
                <p className="text-sm font-semibold uppercase tracking-[0.24em] text-red-500">Blog</p>
                <h1 className="text-3xl font-semibold tracking-tight text-slate-950">Article introuvable</h1>
                <p className="mx-auto max-w-2xl text-sm leading-7 text-slate-600">
                  Cet article n&apos;est plus disponible ou n&apos;a pas encore ete publie.
                </p>
                <Button asChild className="rounded-full bg-red-600 hover:bg-red-700">
                  <Link to="/blog">Voir les autres articles</Link>
                </Button>
              </CardContent>
            </Card>
          ) : loadError ? (
            <Card className="rounded-[2rem] border border-slate-200 bg-white shadow-sm">
              <CardContent className="space-y-4 px-8 py-16 text-center">
                <p className="text-sm font-semibold uppercase tracking-[0.24em] text-red-500">Blog</p>
                <h1 className="text-3xl font-semibold tracking-tight text-slate-950">Chargement indisponible</h1>
                <p className="mx-auto max-w-2xl text-sm leading-7 text-slate-600">
                  Nous n&apos;avons pas pu charger cet article pour le moment. Vous pouvez revenir au blog et reessayer.
                </p>
                <Button asChild className="rounded-full bg-red-600 hover:bg-red-700">
                  <Link to="/blog">Retour au blog</Link>
                </Button>
              </CardContent>
            </Card>
          ) : post ? (
            <article className="space-y-8">
              <Card className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_28px_80px_-55px_rgba(15,23,42,0.45)]">
                {post.cover_image_url ? (
                  <img
                    src={post.cover_image_url}
                    alt={post.title || 'Article Coli Noki'}
                    loading="lazy"
                    className="h-[280px] w-full object-cover sm:h-[360px]"
                  />
                ) : (
                  <div className="flex h-[280px] items-end bg-gradient-to-br from-red-500 via-red-600 to-cyan-500 p-8 sm:h-[360px]">
                    <div className="rounded-3xl bg-white/15 px-4 py-3 text-white backdrop-blur">
                      <p className="text-xs font-semibold uppercase tracking-[0.28em] text-white/80">Coli Noki Blog</p>
                      <p className="mt-2 max-w-xl text-2xl font-semibold">{post.title}</p>
                    </div>
                  </div>
                )}

                <CardContent className="space-y-8 p-6 sm:p-8 lg:p-10">
                  <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                    <span className="inline-flex items-center rounded-full bg-red-50 px-3 py-1 font-medium text-red-600">
                      <CalendarDays className="mr-2 h-4 w-4" />
                      {formatBlogDate(post.created_at)}
                    </span>
                    <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-600">
                      <UserRound className="mr-2 h-4 w-4" />
                      {post.author || 'Equipe Coli Noki'}
                    </span>
                    {post.category ? (
                      <span className="inline-flex items-center rounded-full bg-cyan-50 px-3 py-1 font-medium text-cyan-700">
                        {post.category}
                      </span>
                    ) : null}
                  </div>

                  <div className="space-y-4">
                    <h1 className="text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl">{post.title}</h1>
                    {post.excerpt ? (
                      <p className="max-w-3xl text-lg leading-8 text-slate-600">{post.excerpt}</p>
                    ) : null}
                  </div>

                  <div className="rounded-[1.75rem] border border-slate-200/80 bg-slate-50/80 p-6 sm:p-8">
                    <BlogPostContent content={post.content} emptyMessage="Le contenu de cet article est vide." />
                  </div>

                  {Array.isArray(post.internal_links) && post.internal_links.length > 0 ? (
                    <div className="rounded-[1.25rem] border border-red-100 bg-red-50/70 p-5">
                      <p className="text-sm font-semibold uppercase tracking-[0.2em] text-red-600">Liens utiles</p>
                      <div className="mt-4 flex flex-wrap gap-3">
                        {post.internal_links.map((item) => (
                          <Button
                            key={`${item.url}-${item.label}`}
                            asChild
                            variant="outline"
                            className="rounded-full border-red-100 bg-white text-red-700 hover:bg-red-50"
                          >
                            <Link to={item.url || '/services'}>
                              {item.label || 'Voir la ressource'}
                              <ArrowRight className="ml-2 h-4 w-4" />
                            </Link>
                          </Button>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            </article>
          ) : null}
        </div>
      </main>

      <FooterNew />
    </div>
  );
};

export default BlogPostPage;
